import { useEffect, useRef, useState } from 'react';

interface CustomerTypeSectionProps {
  onContactClick: () => void;
}

export default function CustomerTypeSection({ onContactClick }: CustomerTypeSectionProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="customer-type" ref={ref} className="py-24" style={{ background: '#F5F8FB' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#2BC0D0' }}>WHO WE SERVE</div>
          <h2 className="text-4xl font-black text-gray-900 mb-3">어떤 고객이신가요?</h2>
          <p className="text-gray-500 text-base">고객 유형에 맞는 최적의 솔루션을 제공합니다</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Card 1 - 농가/생산자 */}
          <div
            className={`bg-white rounded-3xl overflow-hidden border border-gray-100 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
          >
            {/* Image */}
            <div className="relative h-72 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/ab02442a1b0014b618fe6976cd463865.jpg"
                alt="농가 생산자 패키징"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,80,140,0.5) 0%, transparent 60%)' }} />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: '#70C6A3' }}>
                  농가 · 생산자
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-7">
              <h3 className="text-2xl font-black text-gray-900 mb-2">빠른 출하가 필요하신가요?</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                수확 후 빠르게 출하 가능한 패키지 준비가 필요한 농가·생산자를 위한 솔루션입니다.팩 공급부터 라벨 부착까지 일괄 처리해 드립니다.
              </p>

              <div className="flex flex-col gap-3 mb-7">
                {[
                  { icon: 'ri-box-3-line', text: '과일별 플라스틱 팩 공급', color: '#0A508C' },
                  { icon: 'ri-price-tag-3-line', text: '출하용 라벨 디자인 & 제작', color: '#3A7CC0' },
                  { icon: 'ri-pushpin-2-line', text: '라벨 부착 완료 후 납품', color: '#2BC0D0' },
                  { icon: 'ri-truck-line', text: '빠른 납기, 소량 주문 가능', color: '#70C6A3' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: item.color }}>
                      <i className={`${item.icon} text-white text-sm`} />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              <a
                href="https://smartstore.naver.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #0A508C, #3A7CC0)' }}
              >
                <i className="ri-store-2-line" />
                스마트스토어에서 바로 구매하기
              </a>
            </div>
          </div>

          {/* Card 2 - 협회/브랜드 */}
          <div
            className={`bg-white rounded-3xl overflow-hidden border border-gray-100 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            {/* Image */}
            <div className="relative h-72 overflow-hidden">
              <img
                src="https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/839caa806d2e99f5404bf9888abb72be.png"
                alt="협회 브랜드 패키징"
                className="w-full h-full object-cover"
                style={{ transform: 'translateY(0px)' }}
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(58,124,192,0.5) 0%, transparent 60%)' }} />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: '#2BC0D0' }}>
                  협회 · 브랜드 · 수출
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-7">
              <h3 className="text-2xl font-black text-gray-900 mb-2">브랜딩이 필요하신가요?</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                브랜드 홍보 및 수출을 준비하는 협회·브랜드 조직을 위한 솔루션입니다.패키지 솔루션에 굿즈 제작까지 함께 지원합니다.
              </p>

              <div className="flex flex-col gap-3 mb-7">
                {[
                  { icon: 'ri-box-3-line', text: '브랜드 전용 패키지 솔루션', color: '#0A508C' },
                  { icon: 'ri-palette-line', text: '브랜드 아이덴티티 라벨 디자인', color: '#3A7CC0' },
                  { icon: 'ri-gift-2-line', text: '홍보용 굿즈 제작 (보조 서비스)', color: '#2BC0D0' },
                  { icon: 'ri-global-line', text: '수출용 패키징 대응 가능', color: '#70C6A3' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: item.color }}>
                      <i className={`${item.icon} text-white text-sm`} />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={onContactClick}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #3A7CC0, #2BC0D0)' }}
              >
                <i className="ri-customer-service-2-line" />
                맞춤 상담 신청하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
